/**
 * NoteScheduleTable — a note's schedule laid out like the CONTRACT amortization schedule
 * (components/contracts/AmortizationScheduleTable.js): Seq · From · To · Payment Date · Flow ·
 * Narration · Rate % · Days · amounts · Payment Amount · Outstanding Balance, with the same row
 * types (Interest Accrual / Interest Payment / Capital Payment), chips, amount colouring and
 * striping from utils/narration.js.
 *
 * Shown either as the TOTAL across every investor (aggregated by the API: payments by date,
 * accruals by period) or for one investor.
 *
 * Notes differ from contracts in two deliberate ways:
 *   - money columns are Gross Interest / Net Interest / Commission (the note's spread), not a
 *     single Interest Amount;
 *   - a "Total paid" row sums the PAID rows only (Interest / Capital Payment). Accrual rows say
 *     when interest is earned; adding them would count every amount twice.
 */
import { useMemo, useState } from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, ToggleButton,
  ToggleButtonGroup, TextField, MenuItem, Alert, Typography, Chip, TablePagination, Tooltip,
} from '@mui/material';
import { formatNumber, formatShortDate } from '../../utils/formatters';
import {
  getNarrationFlowType, getNarrationFlowLabel, getNarrationChipProps, formatNarrationAmount,
  getNarrationRowSx,
} from '../../utils/narration';

const PAID_KINDS = new Set(['interest', 'capital']);
const NARRATION_BY_KIND = { accrual: 'Interest Accrual', interest: 'Interest Payment', capital: 'Capital Payment' };
const narrationOf = (r) => r.narration || NARRATION_BY_KIND[r.row_kind] || r.row_kind;
const num = (v) => Number(v) || 0;
const ALL = -1;

const head = { fontWeight: 600, whiteSpace: 'nowrap' };

// Accrual rows: plain WHT. Payment rows: purple parentheses (cash to the revenue authority),
// exactly as the contract schedule renders it.
const formatWht = (value, flowType) => {
  const wht = num(value);
  if (wht === 0) return '-';
  if (flowType === 'accrual') return formatNumber(wht, 2);
  return <span style={{ color: '#9c27b0' }}>({formatNumber(wht, 2)})</span>;
};

/**
 * Running balance, as the contract table computes it: capital outstanding plus gross interest
 * accrued and not yet paid. Each investor's amount comes in on their start date (notes have no
 * Capital Drawdown row).
 */
const runningBalances = (rows, openings) => {
  const pending = [...openings].sort((a, b) => a.date.localeCompare(b.date));
  let balance = 0;
  return rows.map((r) => {
    const at = r.period_start || r.payment_date;
    while (pending.length && pending[0].date <= at) balance += pending.shift().amount;
    const flow = getNarrationFlowType(narrationOf(r));
    if (flow === 'accrual') balance += num(r.gross_interest);
    else if (flow === 'outflow') balance -= num(r.capital_amount) + num(r.gross_interest);
    return Math.round(balance * 100) / 100;
  });
};

// A leg's start date: its first accrual's period start (or, for a schedule stored before
// accrual rows existed, its first payment row's period start).
const legOpening = (leg) => {
  const rows = leg.rows || [];
  const starts = rows.map((r) => r.period_start).filter(Boolean).sort();
  const first = starts[0] || rows[0]?.payment_date;
  return first ? { date: first, amount: num(rows[0]?.nominal) } : null;
};

// T3-155: a payment row's settlement, from the cash matched against it (Payment Matching → Notes).
// Same chip vocabulary as the contract schedule's Status column.
const settlementChip = (r) => {
  if (!PAID_KINDS.has(r.row_kind) || r.investor_paid === undefined) return '-';
  const owed = num(r.total_payment);
  const paid = num(r.investor_paid);
  if (owed > 0 && paid >= owed - 0.01) return <Chip size="small" color="success" label="Paid" />;
  if (paid > 0) return <Chip size="small" color="warning" variant="outlined" label="Part paid" />;
  return <Chip size="small" color="warning" variant="outlined" label="Pending" />;
};

/* From / To. Accrual rows show their own block. Payment rows (which, as on a contract, have no
   period of their own) show the period they PAY FOR — pays_from / pays_to from the API — in muted
   italic; on the total view, where investors with different start dates combine, the widest span
   is shown with a "varies" chip listing each investor's period. */
const periodCells = (r, codeOf) => {
  if (r.period_start || r.period_end) {
    return [
      r.period_start ? formatShortDate(r.period_start) : '-',
      r.period_end ? formatShortDate(r.period_end) : '-',
    ];
  }
  if (!r.pays_from && !r.pays_to) return ['-', '-'];
  const muted = (v) => <Typography component="span" variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>{v}</Typography>;
  const to = (
    <>
      {muted(r.pays_to ? formatShortDate(r.pays_to) : '-')}
      {r.pays_varies && (
        <Tooltip title={(r.pays_by_leg || []).map((x) => `${codeOf(x.leg_id)}: ${formatShortDate(x.from)} – ${formatShortDate(x.to)}`).join(' · ')}>
          <Chip size="small" variant="outlined" label="varies" sx={{ ml: 0.5, height: 18 }} />
        </Tooltip>
      )}
    </>
  );
  return [muted(r.pays_from ? formatShortDate(r.pays_from) : '-'), to];
};

const NoteScheduleTable = ({ schedule, showSettlement = false }) => {
  const [view, setView] = useState('total');
  const [legId, setLegId] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const legs = useMemo(() => schedule?.legs || [], [schedule]);
  const leg = legs.find((l) => l.leg_id === legId) || legs[0];
  const codeOf = (id) => legs.find((l) => l.leg_id === id)?.code || 'Investor';

  const rows = useMemo(() => {
    if (view === 'total') return schedule?.total || [];
    return leg?.rows || [];
  }, [view, schedule, leg]);

  const balances = useMemo(() => {
    const openings = (view === 'total' ? legs : [leg]).filter(Boolean).map(legOpening).filter(Boolean);
    return runningBalances(rows, openings);
  }, [rows, view, legs, leg]);

  const paid = rows.filter((r) => PAID_KINDS.has(r.row_kind));
  const total = (k) => paid.reduce((s, r) => s + num(r[k]), 0);
  const showWht = rows.some((r) => num(r.wht_amount) !== 0);
  const anyPreview = legs.some((l) => l.is_preview);
  const unpriced = legs.filter((l) => l.pricing_error);

  const shown = rowsPerPage === ALL ? rows : rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const offset = rowsPerPage === ALL ? 0 : page * rowsPerPage;
  const columnCount = 13 + (view === 'total' ? 1 : 0) + (showWht ? 1 : 0) + (showSettlement ? 1 : 0);

  const switchView = (v) => { if (v) { setView(v); setPage(0); } };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => switchView(v)}>
          <ToggleButton value="total">Total note</ToggleButton>
          <ToggleButton value="leg" disabled={legs.length === 0}>Per investor</ToggleButton>
        </ToggleButtonGroup>
        {view === 'leg' && legs.length > 0 && (
          <TextField select size="small" label="Investor" value={leg?.leg_id || ''}
            onChange={(e) => { setLegId(e.target.value); setPage(0); }} sx={{ minWidth: 280 }}>
            {legs.map((l) => (
              <MenuItem key={l.leg_id} value={l.leg_id}>{l.code} — {l.investor_name}</MenuItem>
            ))}
          </TextField>
        )}
      </Box>
      {anyPreview && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Draft investors are shown as a preview at the current settings; their figures are fixed when the note is submitted.
        </Alert>
      )}
      {unpriced.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {unpriced.length} investor(s) cannot be priced and are excluded: {unpriced.map((l) => `${l.code} (${l.pricing_error})`).join('; ')}
        </Alert>
      )}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={head}>Seq</TableCell>
              <TableCell sx={head}>From</TableCell>
              <TableCell sx={head}>To</TableCell>
              <TableCell sx={head}>Payment Date</TableCell>
              {view === 'total' && <TableCell sx={head} align="right">Investors</TableCell>}
              <TableCell sx={head}>Flow</TableCell>
              <TableCell sx={head}>Narration</TableCell>
              {showSettlement && <TableCell sx={head}>Status</TableCell>}
              <TableCell sx={head} align="right">Rate %</TableCell>
              <TableCell sx={head} align="right">Days</TableCell>
              <TableCell sx={head} align="right">Capital Amount</TableCell>
              <TableCell sx={head} align="right">Gross Interest</TableCell>
              <TableCell sx={head} align="right">Net Interest</TableCell>
              <TableCell sx={head} align="right">Commission</TableCell>
              {showWht && <TableCell sx={head} align="right">Withholding Tax</TableCell>}
              <TableCell sx={head} align="right">Payment Amount</TableCell>
              <TableCell sx={head} align="right">Outstanding Balance</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shown.map((r, i) => {
              const narration = narrationOf(r);
              const flow = getNarrationFlowType(narration);
              const isAccrual = flow === 'accrual';
              const flowLabel = getNarrationFlowLabel(narration);
              return (
                <TableRow key={`${r.payment_date}-${r.row_kind}-${r.period_start || ''}-${offset + i}`} sx={getNarrationRowSx(narration)}>
                  <TableCell>{offset + i + 1}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{periodCells(r, codeOf)[0]}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{periodCells(r, codeOf)[1]}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatShortDate(r.payment_date)}</TableCell>
                  {view === 'total' && <TableCell align="right">{r.leg_count}</TableCell>}
                  <TableCell>
                    <Chip label={flowLabel.label} size="small" color={flowLabel.color}
                      variant={flowLabel.sx ? 'outlined' : undefined}
                      sx={{ minWidth: 40, fontWeight: 'bold', ...(flowLabel.sx || {}) }} />
                  </TableCell>
                  <TableCell><Chip size="small" label={narration} {...getNarrationChipProps(narration)} /></TableCell>
                  {showSettlement && <TableCell>{settlementChip(r)}</TableCell>}
                  <TableCell align="right">{isAccrual && r.gross_rate_applied != null ? num(r.gross_rate_applied).toFixed(2) : '-'}</TableCell>
                  <TableCell align="right">{num(r.days) > 0 ? r.days : '-'}</TableCell>
                  <TableCell align="right">{formatNarrationAmount(r.capital_amount, flow)}</TableCell>
                  <TableCell align="right">{formatNarrationAmount(r.gross_interest, flow)}</TableCell>
                  <TableCell align="right">{formatNarrationAmount(r.client_interest, flow)}</TableCell>
                  <TableCell align="right">{num(r.commission_amount) === 0 ? '-' : formatNumber(r.commission_amount, 2)}</TableCell>
                  {showWht && <TableCell align="right">{formatWht(r.wht_amount, flow)}</TableCell>}
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{formatNarrationAmount(r.total_payment, flow)}</TableCell>
                  <TableCell align="right">{formatNumber(balances[offset + i], 2)}</TableCell>
                </TableRow>
              );
            })}
            {rows.length > 0 && (
              <TableRow sx={{ '& td': { fontWeight: 600, borderTop: 2, borderColor: 'divider' } }}>
                <TableCell colSpan={(view === 'total' ? 9 : 8) + (showSettlement ? 1 : 0)}>Total paid</TableCell>
                <TableCell align="right">{formatNumber(total('capital_amount'), 2)}</TableCell>
                <TableCell align="right">{formatNumber(total('gross_interest'), 2)}</TableCell>
                <TableCell align="right">{formatNumber(total('client_interest'), 2)}</TableCell>
                <TableCell align="right">{formatNumber(total('commission_amount'), 2)}</TableCell>
                {showWht && <TableCell align="right">{formatNumber(total('wht_amount'), 2)}</TableCell>}
                <TableCell align="right">{formatNumber(total('total_payment'), 2)}</TableCell>
                <TableCell />
              </TableRow>
            )}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columnCount}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    No schedule rows yet.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {rows.length > 25 && (
        <TablePagination
          component="div"
          count={rows.length}
          page={rowsPerPage === ALL ? 0 : page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[25, 50, 100, { label: 'All', value: ALL }]}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      )}
    </Box>
  );
};

export default NoteScheduleTable;
